import { useEffect, useState } from "react";
import { BookOpen, ArrowUpRight } from "lucide-react";
import PdfFlipbook from "./PdfFlipbook";
import { BACKEND_URL } from "@/lib/api";
import { eventApi } from "@/lib/eventHub";

/**
 * Compact "Program Library" panel. Shows Friday and/or Saturday booklet cards
 * inline (e.g. above the Check-In form) with an embedded PDF flipbook modal.
 *
 * Props:
 *   lockedDay?: "2026-07-17" | "2026-07-18" — show only that day's booklet
 *   className?: string
 *   heading?: string
 */
export default function ProgramBookletCompact({ lockedDay = null, className = "", heading = "Tonight’s Program" }) {
  const [booklet, setBooklet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/event/program-booklet`);
        const data = await res.json();
        if (m) setBooklet(data?.booklet || null);
      } catch { /* noop */ }
      if (m) setLoading(false);
    })();
    return () => { m = false; };
  }, []);

  if (loading || !booklet) return null;

  const openBook = (view) => {
    const title = view === "friday" ? booklet.subtitle_friday : booklet.subtitle_saturday;
    const pdf_url = view === "friday" ? booklet.friday_pdf_url : booklet.saturday_pdf_url;
    const size = view === "friday" ? booklet.friday_size : booklet.saturday_size;
    const pages = view === "friday" ? booklet.friday_pages.length : booklet.saturday_pages.length;
    const toc = view === "friday" ? (booklet.toc_friday || []) : (booklet.toc_saturday || []);
    setOpen({
      title,
      pdf_url,
      file_size: size,
      page_count: pages,
      filename_original: booklet.filename_original,
      toc,
      view,
    });
    try { eventApi.track("program_open", { view, source: "checkin" }); } catch { /* noop */ }
  };

  // Which cards to show
  const showFriday = !lockedDay || lockedDay === "2026-07-17";
  const showSaturday = !lockedDay || lockedDay === "2026-07-18";

  return (
    <>
      <section
        data-testid="checkin-program-library"
        className={`rounded-2xl border-2 border-fuchsia-200 bg-white p-4 ${className}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex h-8 w-8 rounded-lg bg-fuchsia-100 text-fuchsia-700 items-center justify-center">
            <BookOpen size={14} />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-700">
              Program Library
            </div>
            <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">
              {heading}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {showFriday && (
            <BookletTile
              onClick={() => openBook("friday")}
              cover={`${BACKEND_URL}${booklet.cover_url}`}
              accent="fuchsia"
              night="Night One · VIP"
              title="Friday, Jul 17"
              subtitle={booklet.subtitle_friday}
              pages={booklet.friday_pages.length}
              size={booklet.friday_size}
              testid="checkin-booklet-friday"
            />
          )}
          {showSaturday && (
            <BookletTile
              onClick={() => openBook("saturday")}
              cover={`${BACKEND_URL}${booklet.cover_url}`}
              accent="emerald"
              night="Main Event"
              title="Saturday, Jul 18"
              subtitle={booklet.subtitle_saturday}
              pages={booklet.saturday_pages.length}
              size={booklet.saturday_size}
              testid="checkin-booklet-saturday"
            />
          )}
        </div>
      </section>
      {open && <PdfFlipbook doc={open} toc={open.toc || []} onClose={() => setOpen(null)} />}
    </>
  );
}

function BookletTile({ onClick, cover, accent, night, title, subtitle, pages, size, testid }) {
  const badgeBg = accent === "fuchsia" ? "bg-fuchsia-600" : "bg-emerald-600";
  const glow = accent === "fuchsia" ? "hover:shadow-fuchsia-600/40" : "hover:shadow-emerald-700/40";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group relative w-full text-left rounded-2xl overflow-hidden bg-emerald-950 shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${glow}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={cover}
          alt={`Cover: ${title}`}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/60 to-emerald-950/20" />
        <div className={`absolute top-2 left-2 inline-flex items-center gap-1 rounded-full ${badgeBg} px-2 py-0.5 shadow`}>
          <span className="text-[9px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-white">{night}</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="text-[9px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-300 mb-0.5 line-clamp-1">
            {subtitle}
          </div>
          <div className="font-[Cormorant_Garamond] text-lg text-white leading-tight">{title}</div>
          <div className="mt-2 flex items-center justify-between">
            <div className="inline-flex items-center gap-1 rounded-full bg-white text-emerald-950 px-2.5 py-1 text-[10px] font-[Outfit] font-bold shadow">
              Open <ArrowUpRight size={11} />
            </div>
            <div className="text-[9px] font-[Outfit] font-semibold text-white/60 uppercase tracking-widest">
              {pages}p · {(size / (1024 * 1024)).toFixed(1)}MB
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
